import { useAuthStore } from "@/stores/authStore";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { Link } from "react-router-dom";
import { ShoppingCart, Users, Plane, TrendingUp, Calendar, Clock, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { OrderStatus } from "@ckgo2/shared/types";
import i18n, { uiLocale } from "@/i18n";
import { ORDER_STATUS_CONFIG } from "@/lib/statusConfig";
import MyDayPanel from "@/components/dashboard/MyDayPanel";

interface DashboardStats {
  newInquiries: number;
  todayInquiries: number;
  activeTrips: number;
  departingThisMonth: number;
  totalCustomers: number;
  newCustomersThisMonth: number;
  revenueThisMonth: number;
  revenueChange: number;
}

interface RecentInquiry {
  id: string;
  orderNumber: string;
  customer: string;
  email?: string;
  trip: string;
  status: string;
  createdAt: string;
}

interface UpcomingDeparture {
  id: string;
  trip: string;
  date: string;
  participants: string;
  guide: string;
}

interface DashboardRecent {
  recentInquiries: RecentInquiry[];
  upcomingDepartures: UpcomingDeparture[];
  alerts: string[];
}

const dashboardCurrencyFormatter = new Intl.NumberFormat("cs-CZ", {
  style: "currency",
  currency: "CZK",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatDashboardCurrency(amount: number) {
  return dashboardCurrencyFormatter.format(amount);
}

function formatRelativeTime(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return i18n.t("system.dashboard.relativeTime.justNow");
  if (diffMins < 60) return i18n.t("system.dashboard.relativeTime.minutes", { mins: diffMins });
  if (diffHours < 24) return i18n.t("system.dashboard.relativeTime.hours", { hrs: diffHours });
  return i18n.t("system.dashboard.relativeTime.days", { count: diffDays });
}

function StatusBadge({ status }: { status: string }) {
  const entry = ORDER_STATUS_CONFIG[status as OrderStatus];
  if (entry) {
    return <span className={`badge ${entry.className}`}>{entry.label}</span>;
  }
  return (
    <span className="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
      {status}
    </span>
  );
}

const COLOR_CLASS_MAP: Record<string, string> = {
  primary: "bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400",
  accent: "bg-accent-100 text-accent-600 dark:bg-accent-900/30 dark:text-accent-400",
  success: "bg-success-100 text-success-600 dark:bg-success-900/30 dark:text-success-400",
  warning: "bg-warning-100 text-warning-600 dark:bg-warning-900/30 dark:text-warning-400",
};

function getColorClasses(color: string) {
  return COLOR_CLASS_MAP[color] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
}

function DashboardStatsGrid({ stats }: { stats: DashboardStats }) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {t("system.dashboard.stats.newInquiries")}
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
              {stats.newInquiries}
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t("system.dashboard.stats.todayCount", { count: stats.todayInquiries })}
            </p>
          </div>
          <div className={`rounded-full p-3 ${getColorClasses("primary")}`}>
            <ShoppingCart className="h-6 w-6" />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {t("system.dashboard.stats.activeTrips")}
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
              {stats.activeTrips}
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t("system.dashboard.stats.departingThisMonth", { count: stats.departingThisMonth })}
            </p>
          </div>
          <div className={`rounded-full p-3 ${getColorClasses("accent")}`}>
            <Plane className="h-6 w-6" />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {t("system.dashboard.stats.customers")}
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
              {stats.totalCustomers.toLocaleString(uiLocale())}
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t("system.dashboard.stats.newThisMonth", { count: stats.newCustomersThisMonth })}
            </p>
          </div>
          <div className={`rounded-full p-3 ${getColorClasses("success")}`}>
            <Users className="h-6 w-6" />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {t("system.dashboard.stats.revenueThisMonth")}
            </p>
            <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
              {formatDashboardCurrency(stats.revenueThisMonth)}
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t("system.dashboard.stats.vsLastMonth", {
                change: `${stats.revenueChange >= 0 ? "+" : ""}${stats.revenueChange}`,
              })}
            </p>
          </div>
          <div className={`rounded-full p-3 ${getColorClasses("warning")}`}>
            <TrendingUp className="h-6 w-6" />
          </div>
        </div>
      </div>
    </div>
  );
}

function RecentInquiriesCard({
  isLoading,
  inquiries,
}: {
  isLoading: boolean;
  inquiries: RecentInquiry[];
}) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("system.dashboard.recentInquiries")}
        </h2>
        <Link
          to="/orders"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 text-sm font-medium"
        >
          {t("system.dashboard.viewAll")}
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
          ))}
        </div>
      ) : inquiries.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          {t("system.dashboard.noRecentInquiries")}
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.slice(0, 5).map((inquiry) => (
            <Link
              key={inquiry.id}
              to={`/orders/${inquiry.id}`}
              className="flex items-center justify-between rounded-lg border border-gray-100 p-3 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              <div className="flex items-center gap-3">
                <div className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 flex h-10 w-10 items-center justify-center rounded-full font-medium">
                  {inquiry.customer
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)}
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{inquiry.customer}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{inquiry.trip}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <Clock className="h-4 w-4" />
                  {formatRelativeTime(inquiry.createdAt)}
                </span>
                <StatusBadge status={inquiry.status} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function UpcomingDeparturesCard({
  isLoading,
  departures,
}: {
  isLoading: boolean;
  departures: UpcomingDeparture[];
}) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("system.dashboard.upcomingDepartures")}
        </h2>
        <Link
          to="/trips"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 text-sm font-medium"
        >
          {t("system.dashboard.viewAll")}
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
          ))}
        </div>
      ) : departures.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          {t("system.dashboard.noUpcomingDepartures")}
        </div>
      ) : (
        <div className="space-y-3">
          {departures.map((departure) => (
            <div
              key={departure.id}
              className="flex items-center justify-between rounded-lg border border-gray-100 p-3 dark:border-gray-700"
            >
              <div className="flex items-center gap-3">
                <div className="bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400 flex h-10 w-10 items-center justify-center rounded-full">
                  <Calendar className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{departure.trip}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{departure.guide}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(departure.date).toLocaleDateString(uiLocale(), {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("system.dashboard.participants", { participants: departure.participants })}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuthStore();
  const { t } = useTranslation();

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => api.get<ApiResponse<DashboardStats>>("/api/admin/dashboard/stats"),
  });

  const { data: recentData, isLoading: recentLoading } = useQuery({
    queryKey: ["dashboard-recent"],
    queryFn: () => api.get<ApiResponse<DashboardRecent>>("/api/admin/dashboard/recent"),
  });

  const stats = statsData?.data;
  const recent = recentData?.data;

  const isLoading = statsLoading || recentLoading;

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t("system.dashboard.welcome", { name: user?.name?.split(" ")[0] })}
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">{t("system.dashboard.overview")}</p>
      </div>

      {/* Stats grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-16 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          ))}
        </div>
      ) : stats ? (
        <DashboardStatsGrid stats={stats} />
      ) : null}

      {/* My Day — tasks + AI briefing (B2.2) */}
      <MyDayPanel />

      {/* Two columns */}
      <div className="grid gap-6 lg:grid-cols-2">
        <RecentInquiriesCard isLoading={isLoading} inquiries={recent?.recentInquiries || []} />
        <UpcomingDeparturesCard
          isLoading={isLoading}
          departures={recent?.upcomingDepartures || []}
        />
      </div>

      {/* Alerts / Reminders */}
      {!isLoading && recent?.alerts && recent.alerts.length > 0 && (
        <div className="card border-warning-200 bg-warning-50 dark:border-warning-800 dark:bg-warning-900/20">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-warning-600 dark:text-warning-400 h-5 w-5" />
            <div>
              <h3 className="text-warning-800 dark:text-warning-300 font-medium">
                {t("system.dashboard.reminders")}
              </h3>
              <ul className="text-warning-700 dark:text-warning-400 mt-2 space-y-1 text-sm">
                {recent.alerts.map((alert, index) => (
                  <li key={index}>• {alert}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
